import { useState } from 'react';
import { Panel } from '@/components/Panel';
import { useIncidents } from '@/hooks/useIncidents';
import { StatusBadge, EmptyState, Spinner, Pagination } from '@00start/ui';

const STATUS_OPTIONS = ['open', 'contained', 'resolved', 'closed'];
const PAGE_SIZE = 20;

function severityColorKey(severity: string): string {
  if (severity === 'P0' || severity === 'P1') return 'critical';
  if (severity === 'P2') return 'high';
  return 'low';
}

export function IncidentList() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [page, setPage] = useState(1);
  const { incidents, loading, error } = useIncidents(statusFilter);

  const totalPages = Math.max(1, Math.ceil(incidents.length / PAGE_SIZE));
  const pageIncidents = incidents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setStatusFilter(e.target.value);
    setPage(1);
  }

  return (
    <Panel title="Incidents" wide mobilePriority={1} headerAction={
      <select
        data-testid="ops-incidents-status-filter"
        value={statusFilter}
        onChange={handleStatusChange}
        className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-300"
      >
        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    }>
      {loading ? (
        <div className="flex justify-center py-4"><Spinner size="sm" /></div>
      ) : error ? (
        <EmptyState title="Error loading incidents" description={error} />
      ) : incidents.length === 0 ? (
        <EmptyState title={`No ${statusFilter} incidents`} description="Nothing to display for this status." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-slate-500 border-b border-slate-800">
                  <th className="text-left py-2 pr-3">Sev</th>
                  <th className="text-left py-2 pr-3">ID</th>
                  <th className="text-left py-2 pr-3">Trigger</th>
                  <th className="text-left py-2 pr-3">Opened</th>
                  <th className="text-left py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {pageIncidents.map((inc) => (
                  <tr key={inc.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="py-2 pr-3">
                      <StatusBadge
                        label={inc.severity}
                        colorKey={severityColorKey(inc.severity)}
                        variant="severity"
                        className="text-[10px]"
                      />
                    </td>
                    <td className="py-2 pr-3 font-mono text-slate-400 truncate max-w-[120px]">{inc.id}</td>
                    <td className="py-2 pr-3 text-slate-400">{inc.trigger_type}</td>
                    <td className="py-2 pr-3 text-slate-500">{new Date(inc.opened_at).toLocaleDateString()}</td>
                    <td className="py-2 text-slate-400 capitalize">{inc.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div data-testid="ops-incidents-pagination" className="mt-3 flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
